import { action, computed, makeObservable, observable } from 'mobx';
import { Group, GroupSource } from 'types/groupService';
import { bpHistory } from 'global/bpHistory';
import { DashboardEnrollmentPresenter } from 'types/enrollmentService';
import { DashboardGroupPresenter, GroupAction, GroupInteractionController, InboundLeadController } from './types';

interface DelegateCreationParams {
  group: Group;
  inboundLeadModalStore: InboundLeadController;
  groupInteractionDelegate: GroupInteractionController;
  enrollments?: DashboardEnrollmentPresenter[];
}

export class DashboardGroupDelegate {
  private readonly _data: DashboardGroupPresenter;
  private readonly _groupInteractionDelegate: GroupInteractionController;
  public readonly inboundLeadModalStore: InboundLeadController;

  public get data(): DashboardGroupPresenter {
    return this._data;
  }

  public get isInbound(): boolean {
    return this._data.source === GroupSource.ESTORE;
  }

  public get hasNoAOR(): boolean {
    return !this._data.agentOfRecord;
  }

  public onGroupClick(): void {
    if (this.isInbound && this.hasNoAOR) {
      this.inboundLeadModalStore.setGroupData(this._data.id, this._data.name);
    } else {
      bpHistory.push(`group/${this.data.id}`);
    }
  }

  public archiveGroup(): void {
    this._groupInteractionDelegate.commitGroupAction(GroupAction.ARCHIVE, this._data.id, this._data.name);
  }

  public removeGroup(): void {
    this._groupInteractionDelegate.commitGroupAction(GroupAction.REMOVE, this._data.id, this._data.name);
  }

  constructor({
    group: { id, name, agentOfRecord, assigned, source, lastUpdateTime },
    inboundLeadModalStore,
    groupInteractionDelegate,
    enrollments = [],
  }: DelegateCreationParams) {
    if (enrollments && enrollments.length > 0 && enrollments[0].groupId !== id) {
      throw new Error(
        `DashboardGroupDelegate received incompatible group and enrollments! The group id is ${id}, and enrollments' group id is ${enrollments[0].groupId}`,
      );
    }
    this._data = {
      id,
      name,
      agentOfRecord,
      assigned,
      source,
      lastUpdateTime,
      enrollments,
    };
    this.inboundLeadModalStore = inboundLeadModalStore;
    this._groupInteractionDelegate = groupInteractionDelegate;

    makeObservable<DashboardGroupDelegate, '_data'>(this, {
      _data: observable,

      data: computed,
      isInbound: computed,
      hasNoAOR: computed,
      onGroupClick: action.bound,
      archiveGroup: action.bound,
      removeGroup: action.bound,
    });
  }
}
